import { useRef } from 'react';
import PropTypes from 'prop-types';
import { Button, Input, InputNumber, Select, Tooltip } from 'antd';
import { DeleteOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { ChipsRow, insertable } from './placeholders';
import styles from './outreach.module.scss';

// The rule as a form of sections. What can be chosen comes from the engine's catalogue,
// so a rule can only be built from facts the engine actually computes; the engine
// validates again on save.

const MODE_OPTIONS = [
  { value: 'all', label: 'SVE' },
  { value: 'any', label: 'BAR JEDNO' },
];

const MODE_HELP =
  'SVE = moraju važiti svi uslovi (I). BAR JEDNO = dovoljan je jedan (ILI).';
const PRIORITY_HELP =
  'Manji broj = važnije. Ako istog dana važi više pravila, korisnik dobija samo poruku sa najmanjim brojem.';

export const emptyCondition = (catalog) => {
  const fact =
    catalog.facts.find((f) => f.key === 'accountDays') || catalog.facts[0];
  return { fact: fact.key, op: '>=', value: 7 };
};

export const emptyRule = (catalog) => ({
  name: '',
  kind: 'habits',
  priority: 100,
  when: { mode: 'all', conditions: [emptyCondition(catalog)], groups: [] },
  repeat: { mode: 'cooldown', days: 14 },
  instruction: '',
  fallback: '',
});

const ARRAY_OPS = ['in', 'notIn', 'includes', 'notIncludes'];

const opsFor = (catalog, fact) =>
  catalog.ops.filter(
    (o) =>
      o.types.includes(fact.type) &&
      (fact.nullable || !['isEmpty', 'notEmpty'].includes(o.key))
  );

const defaultValueFor = (fact, op) => {
  if (op === 'isEmpty' || op === 'notEmpty') return undefined;
  if (fact.type === 'list' || ARRAY_OPS.includes(op)) return [];
  if (fact.type === 'boolean') return true;
  if (fact.type === 'enum') return Object.keys(fact.values || {})[0];
  return fact.min && fact.min > 0 ? fact.min : 0;
};

const Info = ({ title }) => (
  <Tooltip title={title}>
    <InfoCircleOutlined className={styles.info} tabIndex={0} />
  </Tooltip>
);

Info.propTypes = { title: PropTypes.node.isRequired };

const Section = ({ label, sub, info, children }) => (
  <section className={styles.sec}>
    <div className={styles.secLabel}>
      {label}
      {info && <Info title={info} />}
    </div>
    {sub && <div className={styles.secSub}>{sub}</div>}
    {children}
  </section>
);

Section.propTypes = {
  label: PropTypes.string.isRequired,
  sub: PropTypes.string,
  info: PropTypes.node,
  children: PropTypes.node,
};

const helpOption = (o) => (
  <div>
    <div>{o.data.label}</div>
    {o.data.help && <div className={styles.optHelp}>{o.data.help}</div>}
  </div>
);

// Dropdown labels start with a capital; the same labels stay lower-case mid-sentence.
const cap = (t) => (t ? t.charAt(0).toUpperCase() + t.slice(1) : t);

// A message textarea with an "Ubaci podatak" picker that inserts {key} at the cursor.
const PlaceholderField = ({
  catalog,
  value,
  onChange,
  maxLength,
  minRows,
  placeholder,
  readOnly,
}) => {
  const ref = useRef(null);
  const options = insertable(catalog).map((f) => ({
    value: f.key,
    label: cap(f.label),
    help: f.help,
  }));

  const insert = (key) => {
    const el = ref.current?.resizableTextArea?.textArea;
    const text = `{${key}}`;
    const cur = value || '';
    const at =
      typeof el?.selectionStart === 'number' ? el.selectionStart : null;
    const to = typeof el?.selectionEnd === 'number' ? el.selectionEnd : at;
    const next =
      at === null ? cur + text : cur.slice(0, at) + text + cur.slice(to);
    if (maxLength && next.length > maxLength) return;
    onChange(next);
    if (el && at !== null) {
      setTimeout(() => {
        el.focus();
        el.setSelectionRange(at + text.length, at + text.length);
      }, 0);
    }
  };

  return (
    <div className={styles.fieldBlock}>
      {!readOnly && (
        <Select
          className={styles.insertSelect}
          value={null}
          placeholder="+ Ubaci podatak"
          showSearch
          optionFilterProp="label"
          popupMatchSelectWidth={false}
          virtual={false}
          options={options}
          optionRender={helpOption}
          onChange={insert}
        />
      )}
      <Input.TextArea
        ref={ref}
        className={styles.textarea}
        variant="borderless"
        value={value}
        autoSize={{ minRows, maxRows: 8 }}
        maxLength={maxLength}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        disabled={readOnly}
      />
      <ChipsRow catalog={catalog} text={value} />
    </div>
  );
};

PlaceholderField.propTypes = {
  catalog: PropTypes.object.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  maxLength: PropTypes.number,
  minRows: PropTypes.number,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
};

const idOptions = (values) =>
  Object.entries(values || {}).map(([value, label]) => ({ value, label }));

const ConditionRow = ({ catalog, condition, onChange, onRemove, readOnly }) => {
  const fact =
    catalog.facts.find((f) => f.key === condition.fact) || catalog.facts[0];
  const ops = opsFor(catalog, fact);
  const showUnit =
    fact.unit &&
    !['list', 'boolean', 'enum'].includes(fact.type) &&
    !['in', 'notIn'].includes(condition.op) &&
    !['isEmpty', 'notEmpty'].includes(condition.op);

  const setFact = (key) => {
    const next = catalog.facts.find((f) => f.key === key);
    const nextOps = opsFor(catalog, next);
    const op = nextOps.some((o) => o.key === condition.op)
      ? condition.op
      : (nextOps[0] || {}).key;
    onChange({ fact: key, op, value: defaultValueFor(next, op) });
  };
  const setOp = (op) => {
    const listNow = ARRAY_OPS.includes(condition.op);
    const listNext = ARRAY_OPS.includes(op);
    const keep = listNow === listNext && !['isEmpty', 'notEmpty'].includes(op);
    onChange({
      ...condition,
      op,
      value: keep ? condition.value : defaultValueFor(fact, op),
    });
  };

  let valueInput = null;
  if (!['isEmpty', 'notEmpty'].includes(condition.op)) {
    if (fact.type === 'list') {
      valueInput = (
        <Select
          mode="multiple"
          showSearch
          optionFilterProp="label"
          className={`${styles.valueWide} ${styles.multi}`}
          value={Array.isArray(condition.value) ? condition.value : []}
          onChange={(v) => onChange({ ...condition, value: v })}
          options={idOptions(fact.values)}
          placeholder="izaberite…"
          disabled={readOnly}
        />
      );
    } else if (['in', 'notIn'].includes(condition.op)) {
      valueInput =
        fact.type === 'enum' ? (
          <Select
            mode="multiple"
            className={`${styles.valueWide} ${styles.multi}`}
            value={condition.value || []}
            onChange={(v) => onChange({ ...condition, value: v })}
            options={idOptions(fact.values)}
            disabled={readOnly}
          />
        ) : (
          <Select
            mode="tags"
            className={`${styles.valueWide} ${styles.multi}`}
            value={(condition.value || []).map(String)}
            onChange={(v) =>
              onChange({
                ...condition,
                value: v.map(Number).filter((n) => !Number.isNaN(n)),
              })
            }
            tokenSeparators={[',', ' ']}
            placeholder="npr. 7, 14, 30"
            disabled={readOnly}
          />
        );
    } else if (fact.type === 'boolean') {
      valueInput = (
        <Select
          className={styles.valueNarrow}
          value={condition.value === true ? 'true' : 'false'}
          onChange={(v) => onChange({ ...condition, value: v === 'true' })}
          options={[
            { value: 'true', label: 'da' },
            { value: 'false', label: 'ne' },
          ]}
          disabled={readOnly}
        />
      );
    } else if (fact.type === 'enum') {
      valueInput = (
        <Select
          className={styles.valueEnum}
          value={condition.value}
          onChange={(v) => onChange({ ...condition, value: v })}
          options={idOptions(fact.values)}
          disabled={readOnly}
        />
      );
    } else {
      valueInput = (
        <InputNumber
          className={styles.num}
          value={condition.value}
          min={fact.min}
          max={fact.max}
          onChange={(v) => onChange({ ...condition, value: v })}
          disabled={readOnly}
        />
      );
    }
  }

  return (
    <div className={styles.condition}>
      <Select
        showSearch
        optionFilterProp="label"
        className={styles.factSelect}
        value={condition.fact}
        onChange={setFact}
        virtual={false}
        popupMatchSelectWidth={false}
        options={catalog.facts.map((f) => ({
          value: f.key,
          label: cap(f.label),
          help: f.help,
        }))}
        optionRender={helpOption}
        disabled={readOnly}
      />
      <div className={styles.condRest}>
        <Select
          className={styles.opSelect}
          value={condition.op}
          onChange={setOp}
          options={ops.map((o) => ({ value: o.key, label: o.label }))}
          disabled={readOnly}
        />
        {valueInput}
        {showUnit && <span className={styles.unit}>{fact.unit}</span>}
        {!readOnly && (
          <Button
            type="text"
            className={styles.removeBtn}
            icon={<DeleteOutlined />}
            onClick={onRemove}
            aria-label="Ukloni uslov"
          />
        )}
      </div>
    </div>
  );
};

ConditionRow.propTypes = {
  catalog: PropTypes.object.isRequired,
  condition: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
};

const ModeSelect = ({ value, onChange, disabled }) => (
  <Select
    className={styles.modeSelect}
    value={value}
    onChange={onChange}
    options={MODE_OPTIONS}
    disabled={disabled}
  />
);

ModeSelect.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

// Small dim "i" / "ili" between two items of a condition box.
const Connector = ({ mode }) => (
  <div className={styles.connector}>{mode === 'any' ? 'ili' : 'i'}</div>
);

Connector.propTypes = { mode: PropTypes.string };

const RuleEditor = ({ catalog, value, onChange, readOnly = false }) => {
  const rule = value;
  const set = (patch) => onChange({ ...rule, ...patch });
  const setWhen = (patch) => set({ when: { ...rule.when, ...patch } });

  const updateCondition = (list, i, next) =>
    list.map((c, j) => (j === i ? next : c));
  const updateGroup = (gi, patch) =>
    setWhen({
      groups: rule.when.groups.map((g, j) =>
        j === gi ? { ...g, ...patch } : g
      ),
    });

  const total =
    rule.when.conditions.length +
    rule.when.groups.reduce((a, g) => a + g.conditions.length, 0);
  const canAdd = total < (catalog.limits?.conditions ?? 12);

  const items = [
    ...rule.when.conditions.map((c, i) => ({ c, i })),
    ...rule.when.groups.map((g, gi) => ({ g, gi })),
  ];

  return (
    <div className={styles.editor}>
      <Section
        label="Naziv"
        sub="Kratko ime da biste pravilo lako prepoznali; korisnik ga ne vidi."
      >
        <Input
          className={styles.nameInput}
          value={rule.name}
          maxLength={catalog.limits?.name ?? 80}
          placeholder="Naziv pravila"
          onChange={(e) => set({ name: e.target.value })}
          disabled={readOnly}
        />
      </Section>

      <Section
        label="Kome se šalje"
        sub="Uslovi koje korisnik mora da ispuni danas."
      >
        <div className={styles.line}>
          <span>Važi ako je ispunjeno</span>
          <ModeSelect
            value={rule.when.mode}
            onChange={(mode) => setWhen({ mode })}
            disabled={readOnly}
          />
          <span>od uslova:</span>
          <Info title={MODE_HELP} />
        </div>

        <div className={styles.condBox}>
          {items.map((it, idx) => (
            <div key={it.g ? `g${it.gi}` : `c${it.i}`}>
              {idx > 0 && <Connector mode={rule.when.mode} />}
              {it.g ? (
                <div className={styles.group}>
                  <div className={styles.groupHead}>
                    <span className={styles.secSub}>
                      Grupa – računa se kao jedan uslov
                    </span>
                    {!readOnly && (
                      <Button
                        size="small"
                        type="text"
                        icon={<DeleteOutlined />}
                        aria-label="Ukloni grupu"
                        onClick={() =>
                          setWhen({
                            groups: rule.when.groups.filter(
                              (_, j) => j !== it.gi
                            ),
                          })
                        }
                      >
                        ukloni grupu
                      </Button>
                    )}
                  </div>
                  <div className={styles.line}>
                    <span>Grupa važi ako je ispunjeno</span>
                    <ModeSelect
                      value={it.g.mode}
                      onChange={(mode) => updateGroup(it.gi, { mode })}
                      disabled={readOnly}
                    />
                    <span>od uslova:</span>
                  </div>
                  {it.g.conditions.map((c, i) => (
                    <div key={`g${it.gi}c${i}`}>
                      {i > 0 && <Connector mode={it.g.mode} />}
                      <ConditionRow
                        catalog={catalog}
                        condition={c}
                        readOnly={readOnly}
                        onChange={(next) =>
                          updateGroup(it.gi, {
                            conditions: updateCondition(
                              it.g.conditions,
                              i,
                              next
                            ),
                          })
                        }
                        onRemove={() =>
                          updateGroup(it.gi, {
                            conditions: it.g.conditions.filter(
                              (_, j) => j !== i
                            ),
                          })
                        }
                      />
                    </div>
                  ))}
                  {!readOnly && canAdd && (
                    <Button
                      size="small"
                      type="link"
                      className={styles.addBtn}
                      onClick={() =>
                        updateGroup(it.gi, {
                          conditions: [
                            ...it.g.conditions,
                            emptyCondition(catalog),
                          ],
                        })
                      }
                    >
                      + Dodaj uslov u grupu
                    </Button>
                  )}
                </div>
              ) : (
                <ConditionRow
                  catalog={catalog}
                  condition={it.c}
                  readOnly={readOnly}
                  onChange={(next) =>
                    setWhen({
                      conditions: updateCondition(
                        rule.when.conditions,
                        it.i,
                        next
                      ),
                    })
                  }
                  onRemove={() =>
                    setWhen({
                      conditions: rule.when.conditions.filter(
                        (_, j) => j !== it.i
                      ),
                    })
                  }
                />
              )}
            </div>
          ))}
        </div>

        {!readOnly && (
          <div className={styles.addRow}>
            {canAdd && (
              <Button
                onClick={() =>
                  setWhen({
                    conditions: [
                      ...rule.when.conditions,
                      emptyCondition(catalog),
                    ],
                  })
                }
              >
                + Dodaj uslov
              </Button>
            )}
            {rule.when.groups.length < (catalog.limits?.groups ?? 3) &&
              canAdd && (
                <Tooltip title="Grupa služi za kombinacije, npr. „VIP ILI više od 5 porudžbina“, uz ostale uslove.">
                  <Button
                    onClick={() =>
                      setWhen({
                        groups: [
                          ...rule.when.groups,
                          {
                            mode: 'any',
                            conditions: [emptyCondition(catalog)],
                          },
                        ],
                      })
                    }
                  >
                    + Dodaj grupu
                  </Button>
                </Tooltip>
              )}
          </div>
        )}
      </Section>

      <Section label="Šta se šalje" sub="Vrsta i važnost poruke.">
        <div className={styles.twoCol}>
          <div className={styles.fieldBlock}>
            <div className={styles.subLabel}>
              Vrsta poruke
              <Info title="Određuje ton i svrhu poruke; AI je piše u skladu s tim." />
            </div>
            <Select
              className={styles.kindSelect}
              value={rule.kind}
              onChange={(kind) => set({ kind })}
              options={catalog.kinds.map((k) => ({
                value: k.key,
                label: cap(k.label),
              }))}
              disabled={readOnly}
            />
          </div>
          <div className={styles.fieldBlock}>
            <div className={styles.subLabel}>Prioritet</div>
            <InputNumber
              className={styles.num}
              value={rule.priority}
              min={0}
              max={1000}
              onChange={(v) => set({ priority: v ?? 0 })}
              disabled={readOnly}
            />
            <div className={styles.secSub}>{PRIORITY_HELP}</div>
          </div>
        </div>
      </Section>

      <Section
        label="Koliko često"
        sub="Koliko često isti korisnik može da dobije poruku po ovom pravilu."
      >
        <div className={styles.line}>
          <Select
            className={styles.kindSelect}
            value={rule.repeat.mode}
            onChange={(mode) =>
              set({
                repeat: {
                  mode,
                  days: mode === 'once' ? 0 : rule.repeat.days || 14,
                },
              })
            }
            options={[
              { value: 'once', label: 'Samo jednom po korisniku' },
              { value: 'cooldown', label: 'Najviše jednom u' },
            ]}
            disabled={readOnly}
          />
          {rule.repeat.mode === 'cooldown' && (
            <>
              <InputNumber
                className={styles.num}
                value={rule.repeat.days}
                min={1}
                max={365}
                onChange={(v) =>
                  set({ repeat: { ...rule.repeat, days: v ?? 1 } })
                }
                disabled={readOnly}
              />
              <span className={styles.unit}>dana</span>
            </>
          )}
        </div>
      </Section>

      <Section
        label="Uputstvo za AI"
        sub="AI na osnovu ovoga sam piše poruku. Korisnik ne vidi ovaj tekst."
      >
        <PlaceholderField
          catalog={catalog}
          value={rule.instruction}
          onChange={(instruction) => set({ instruction })}
          maxLength={catalog.limits?.instruction ?? 1000}
          minRows={3}
          readOnly={readOnly}
        />
      </Section>

      <Section
        label="Rezervni tekst"
        sub="Gotova poruka koju korisnik dobija ako AI nije dostupan."
      >
        <PlaceholderField
          catalog={catalog}
          value={rule.fallback}
          onChange={(fallback) => set({ fallback })}
          maxLength={catalog.limits?.fallback ?? 500}
          minRows={2}
          placeholder="npr. {ime}, kako napredujete sa svojom rutinom? Tu smo ako Vam zatreba pomoć."
          readOnly={readOnly}
        />
      </Section>
    </div>
  );
};

RuleEditor.propTypes = {
  catalog: PropTypes.shape({
    facts: PropTypes.array.isRequired,
    ops: PropTypes.array.isRequired,
    kinds: PropTypes.array.isRequired,
    limits: PropTypes.object,
  }).isRequired,
  value: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
};

export default RuleEditor;
